import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Chip
} from '@mui/material';
import useApi from 'hooks/useApi';

interface Field {
  name: string;
  type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
  required: boolean;
  dropdownSource?: 'teams' | 'countryCode';
  dropdownOptions?: string[];
}

interface ResourceType {
  id: string;
  name: string;
  description: string;
  fields: Field[];
  createdAt: string;
  category?: 'user' | 'others';
  access?: string[];
  invite?: boolean;
  allowRating?: boolean;
}

interface ResourceTypeChangerProps {
  open: boolean;
  onClose: () => void;
  selectedResources: any[];
  currentResourceTypeId: string;
  clientUID: string | null;
  onSuccess: () => void;
}

const ResourceTypeChanger: React.FC<ResourceTypeChangerProps> = ({
  open,
  onClose,
  selectedResources,
  currentResourceTypeId,
  clientUID,
  onSuccess
}) => {
  const [newResourceTypeId, setNewResourceTypeId] = useState('');
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [currentResourceType, setCurrentResourceType] = useState<ResourceType | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [fieldCompatibilityError, setFieldCompatibilityError] = useState<string>('');

  const { getResourceTypes, updateResourceData, showToast } = useApi();

  useEffect(() => {
    if (open) {
      loadResourceTypes();
    }
  }, [open]);

  const loadResourceTypes = async () => {
    setLoading(true);
    try {
      const response = await getResourceTypes(clientUID || undefined, null);
      let allResourceTypes: ResourceType[] = [];
      
      if (Array.isArray(response)) {
        allResourceTypes = response;
      } else if (response?.data && Array.isArray(response.data)) {
        allResourceTypes = response.data;
      }

      // Find the current resource type
      const current = allResourceTypes.find(rt => rt.id === currentResourceTypeId);
      setCurrentResourceType(current || null);

      // Filter out the current resource type from the list
      setResourceTypes(allResourceTypes.filter(rt => rt.id !== currentResourceTypeId));
    } catch (error) {
      console.error('Failed to load resource types:', error);
      setResourceTypes([]);
      setCurrentResourceType(null);
      showToast('Failed to load resource types', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Function to check field compatibility between resource types
  const checkFieldCompatibility = (currentType: ResourceType, newType: ResourceType): string => {
    const currentFields = currentType.fields;
    const newFields = newType.fields;

    // Create maps for easier comparison
    const currentFieldMap = new Map(currentFields.map(f => [f.name, f]));
    const newFieldMap = new Map(newFields.map(f => [f.name, f]));

    const incompatibleFields: string[] = [];

    // Check each field in the current type
    for (const currentField of currentFields) {
      const newField = newFieldMap.get(currentField.name);
      
      if (!newField) {
        // Field exists in current but not in new type
        incompatibleFields.push(`Field "${currentField.name}" exists in current type but not in new type`);
        continue;
      }

      // Check if field types match
      if (currentField.type !== newField.type) {
        incompatibleFields.push(`Field "${currentField.name}" has different type (current: ${currentField.type}, new: ${newField.type})`);
      }

      // Check if dropdown sources match for dropdown fields
      if (currentField.type === 'dropdown' && newField.type === 'dropdown') {
        if (currentField.dropdownSource !== newField.dropdownSource) {
          incompatibleFields.push(`Field "${currentField.name}" has different dropdown source (current: ${currentField.dropdownSource || 'none'}, new: ${newField.dropdownSource || 'none'})`);
        }
      }

      // Check if required status matches
      if (currentField.required !== newField.required) {
        incompatibleFields.push(`Field "${currentField.name}" has different required status (current: ${currentField.required}, new: ${newField.required})`);
      }
    }

    // Check for fields that exist in new type but not in current type
    for (const newField of newFields) {
      if (!currentFieldMap.has(newField.name)) {
        if (newField.required) {
          incompatibleFields.push(`New required field "${newField.name}" does not exist in current type`);
        }
      }
    }

    return incompatibleFields.length > 0 ? incompatibleFields.join('; ') : '';
  };

  const handleResourceTypeSelection = (resourceTypeId: string) => {
    setNewResourceTypeId(resourceTypeId);
    
    if (resourceTypeId && currentResourceType) {
      const selectedResourceType = resourceTypes.find(rt => rt.id === resourceTypeId);
      if (selectedResourceType) {
        const compatibilityError = checkFieldCompatibility(currentResourceType, selectedResourceType);
        setFieldCompatibilityError(compatibilityError);
      }
    } else {
      setFieldCompatibilityError('');
    }
  };

  const handleResourceTypeChange = async () => {
    if (!newResourceTypeId) {
      showToast('Please select a new resource type', 'error');
      return;
    }

    if (fieldCompatibilityError) {
      showToast('Cannot change resource type due to field compatibility issues', 'error');
      return;
    }

    setUpdating(true);
    try {
      const promises = selectedResources.map(resource =>
        updateResourceData(resource.id, {
          resourceTypeId: newResourceTypeId,
          data: resource.data
        })
      );

      await Promise.all(promises);
      
      showToast(
        `Successfully updated ${selectedResources.length} resource${selectedResources.length > 1 ? 's' : ''} to new type`, 
        'success'
      );
      
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Failed to update resource types:', error);
      showToast('Failed to update resource types', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleClose = () => {
    setNewResourceTypeId('');
    setFieldCompatibilityError('');
    onClose();
  };

  const selectedResourceType = resourceTypes.find(rt => rt.id === newResourceTypeId);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          Change Resource Type
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          You are about to change the resource type for {selectedResources.length} selected resource{selectedResources.length > 1 ? 's' : ''}. 
          This action cannot be undone.
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Resources:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedResources.slice(0, 5).map((resource, index) => (
              <Chip 
                key={resource.id} 
                label={`Resource ${index + 1}`} 
                size="small" 
                variant="outlined" 
              />
            ))}
            {selectedResources.length > 5 && (
              <Chip 
                label={`+${selectedResources.length - 5} more`} 
                size="small" 
                variant="outlined" 
              />
            )}
          </Box>
        </Box>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>New Resource Type</InputLabel>
          <Select
            value={newResourceTypeId}
            label="New Resource Type"
            onChange={(e) => handleResourceTypeSelection(e.target.value)}
            disabled={loading}
          >
            {resourceTypes.map((resourceType) => (
              <MenuItem key={resourceType.id} value={resourceType.id}>
                {resourceType.name} - {resourceType.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {fieldCompatibilityError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Field Compatibility Issues:
            </Typography>
            <Typography variant="body2">
              {fieldCompatibilityError}
            </Typography>
          </Alert>
        )}

        {selectedResourceType && (
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              New Resource Type Details:
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Category:</strong> {selectedResourceType.category || 'Others'}
            </Typography>
            {selectedResourceType.access && selectedResourceType.access.length > 0 && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Access:</strong> {selectedResourceType.access.join(', ')}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              <strong>Fields:</strong> {selectedResourceType.fields.length} field{selectedResourceType.fields.length > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" disabled={updating}>
          Cancel
        </Button>
        <Button 
          onClick={handleResourceTypeChange} 
          variant="contained" 
          disabled={!newResourceTypeId || updating || !!fieldCompatibilityError}
        >
          {updating ? 'Updating...' : 'Change Resource Type'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResourceTypeChanger;
